// Load the universal BRDF "FG" LUT (Karis split-sum environment BRDF) from the
// baked static asset. The asset is REQUIRED — a missing/corrupt file throws and
// fails hair init loudly; there is no runtime compute fallback.
//
// The LUT is a deterministic, character-independent constant — the same table
// for every character and every session. It used to be built at runtime by
// buildBrdfLut(256,1024) (a 256×256×1024 GGX importance-sampling loop), a heavy
// synchronous main-thread pass that dominated hair init. It is now baked to a
// ~512 KB asset by the vite bake-brdf-fg-lut plugin at every dev/build start
// (tools/bake_brdf_fg_lut.mjs / `npm run bake-brdf-lut`), so at runtime the LUT
// must simply be ready — per the project rule, a missing asset fails loudly
// instead of being silently recomputed on the main thread.
//
// The result is memoised via a cached promise so concurrent mounts (React
// StrictMode double-invoke) and later character re-switches never re-fetch the
// universal table. A FAILED load drops the cached promise so a retry (e.g. a
// character re-switch after a transient network error) can re-fetch.
//
// Output shape { lut: Float32Array(res*res*2), res } feeds BOTH shade paths
// unchanged: makeFgLutSampler (CPU) and hairGpuShader (WGSL storage buffer).

// Universal asset — served from the app's own public/ dir at a fixed path,
// independent of the per-character hair pack's assetsBaseUrl (the table is the
// same for every character, so it is NOT duplicated into each .gsh pack).
const DEFAULT_URL = '/assets/hair_static/brdf_fg_lut_256.bin';
const DEFAULT_RES = 256;

let _promise = null;

async function _load(url, res) {
  try {
    const r = await fetch(url, { cache: 'force-cache' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const buf = await r.arrayBuffer();
    const expected = res * res * 2 * 4;
    if (buf.byteLength !== expected) {
      throw new Error(`size ${buf.byteLength} != expected ${expected} (res=${res})`);
    }
    return { lut: new Float32Array(buf), res };
  } catch (e) {
    throw new Error(
      `[hair] FG LUT asset unavailable (${url}: ${e.message}). The LUT must be ` +
      `baked before the app runs — the vite bake-brdf-fg-lut plugin generates it ` +
      `at dev/build start; \`npm run bake-brdf-lut\` regenerates it by hand.`,
    );
  }
}

/** Resolve the FG LUT once (memoised). Returns { lut, res }; rejects (and
 *  un-memoises, so a later call retries) when the baked asset is missing. */
export function loadFgLut(url = DEFAULT_URL, res = DEFAULT_RES) {
  if (!_promise) {
    _promise = _load(url, res);
    _promise.catch(() => { _promise = null; });
  }
  return _promise;
}

/** Test-only: drop the memoised promise so a spec can exercise both the
 *  asset-hit and asset-miss paths from a clean slate. */
export function __resetFgLutCache() {
  _promise = null;
}
